import { type Static, Type } from "@sinclair/typebox"
import {
  type ArtworkAssetVariant,
  type ArtworkUploadRecord,
  ArtworkUploadRecordSchema,
  type ArtworkUploadStatus,
  NonEmptyStringSchema,
} from "../schemas"

export const ARTWORK_UPLOAD_STORAGE_KEY = "craft-force.artwork-uploads"
export const ARTWORK_UPLOAD_STORAGE_VERSION = 1

export const ArtworkUploadStoreSchema = Type.Array(ArtworkUploadRecordSchema)
export type ArtworkUploadStore = Static<typeof ArtworkUploadStoreSchema>

export const EMPTY_ARTWORK_UPLOAD_STORE: ArtworkUploadStore = []

export const CreateArtworkUploadInputSchema = Type.Object({
  originalFilename: NonEmptyStringSchema,
  mimeType: Type.String({
    minLength: 1,
  }),
  byteLength: Type.Integer({
    minimum: 1,
  }),
})

export type CreateArtworkUploadInput = Static<
  typeof CreateArtworkUploadInputSchema
>

export function createArtworkUploadRecord(
  input: CreateArtworkUploadInput,
): ArtworkUploadRecord {
  return {
    uploadId: `upload_${createSecureOpaqueId()}`,
    originalFilename: input.originalFilename.trim(),
    status: "processing",
    mimeType: input.mimeType,
    byteLength: input.byteLength,
  }
}

function createSecureOpaqueId(): string {
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID().replace(/-/gu, "")
  }

  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  return [...bytes].map((value) => value.toString(16).padStart(2, "0")).join("")
}

export function updateArtworkUploadStatus(
  uploads: readonly ArtworkUploadRecord[],
  uploadId: string,
  status: ArtworkUploadStatus,
  options?: {
    hash?: string
    variants?: readonly ArtworkAssetVariant[]
    rejectionReason?: string
  },
): ArtworkUploadStore {
  return uploads.map((upload) => {
    if (upload.uploadId !== uploadId) return upload

    return {
      ...upload,
      status,
      ...(options?.hash ? { hash: options.hash } : {}),
      ...(options?.variants ? { variants: [...options.variants] } : {}),
      ...(options?.rejectionReason
        ? { rejectionReason: options.rejectionReason }
        : {}),
    }
  })
}

export function upsertArtworkUpload(
  uploads: readonly ArtworkUploadRecord[],
  nextUpload: ArtworkUploadRecord,
): ArtworkUploadStore {
  const existingIndex = uploads.findIndex(
    (upload) => upload.uploadId === nextUpload.uploadId,
  )
  if (existingIndex === -1) {
    return [...uploads, nextUpload]
  }

  return uploads.map((upload, index) =>
    index === existingIndex ? nextUpload : upload,
  )
}
